import React from "react";

import { InlineOption } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { IntegerReviewedCareElement } from "@/Flowsheets/Models/CareElements/IntegerReviewedCareElement";
import { MultiSelectReviewedCareElement } from "@/Flowsheets/Models/CareElements/MultiSelectReviewedCareElement";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import CareElementDateReviewedCheckbox from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedCheckbox";
import CareElementDateReviewedNumber from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedNumber";
import { CareElementOptionsControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedOptionsControl";
import { CareElementYesNoControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedYesNoControl";
import { ReviewedCareElementContainer } from "./CareComponentParts/DateReviewedCareElementComponents/ReviewedDateContainer";
import { ICareComponentProps } from "./Types";

import styles from "./AsthmaBasicCareComponent.module.scss";

interface AsthmaBasicCareComponent extends CareComponent {
    CareElements: Array<IntegerReviewedCareElement | BooleanReviewedCareElement | MultiSelectReviewedCareElement>;
}

function getSideLabel(careElementName: string): string {
    switch (careElementName) {
        case FlowsheetConstants.AsthmaBasic.Monitoring.NumberOfSchoolWorkAbsenceCareElementName:
            return "Flowsheets_AsthmaBasicCareComponentLabel_SinceLastAssessment";
        default:
            return "Flowsheets_AsthmaBasicCareComponentLabel_DaysPerWeek";
    }
}

const AsthmaBasicCareComponent = React.memo(
    ({ isEditable, state, mutateState }: ICareComponentProps<AsthmaBasicCareComponent>): JSX.Element => {
        const { getText } = useTranslation();
        const coreCareElement = state?.CareElements?.toLookup(p => p.SubSectionName) ?? {};
        const keys = Object.keys(coreCareElement);

        const rowIntegers: string[] = [FlowsheetConstants.AsthmaBasic.Exacerbations.ExacerbationsCareElementName];
        const yesOrNoBooleans: string[] = [FlowsheetConstants.AsthmaBasic.Monitoring.SpirometryTestCareElementName, FlowsheetConstants.AsthmaBasic.Monitoring.SchoolWorkAbsenceCareElementName, FlowsheetConstants.AsthmaBasic.Monitoring.LimitsPhysicalActivityCareElementName];

        if (!coreCareElement)
            return null;

        return (
            <div>
                <div className={`${styles.subsectionTitleLabel} ${styles.spanRight} ${styles.subsectionUnderline}`}>{getText("Flowsheets_AsthmaBasicCareComponentLabel_AssessmentAndMonitoring")}</div>
                {keys.map(key => {
                    const careElementsBySection = coreCareElement[key];
                    if (!careElementsBySection || careElementsBySection.length === 0)
                        return null;

                    return (
                        <ReviewedCareElementContainer key={`${FlowsheetConstants.AsthmaBasic.CareComponentName}_${key}`}>
                            <div className={`${styles.subsectionTitleLabel} ${styles.spanRight}`}>{getText(`Flowsheets_AsthmaBasicCareComponent_SubSectionName_${key}`)}</div>

                            {careElementsBySection.map((careElement => {
                                if (careElement.CareElementType === CareElementType.IntegerReviewed) {
                                    return (
                                        <CareElementDateReviewedNumber
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_AsthmaBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            onChange={(element: IntegerReviewedCareElement) => mutateState(state => {
                                                state.CareElements = [...state.CareElements];
                                                const index = state.CareElements.findIndex(p => p.Name === element.Name);
                                                if (index !== -1) {
                                                    state.CareElements[index] = element;
                                                }
                                            })}
                                            type="integer"
                                            side={rowIntegers.includes(careElement.Name)
                                                ? "row"
                                                : "column"
                                            }
                                            unit={getText(getSideLabel(careElement.Name))}
                                        />
                                    );
                                }
                                else if (careElement.CareElementType === CareElementType.BooleanReviewed) {
                                    return (
                                        yesOrNoBooleans.includes(careElement.Name) ? (
                                            <CareElementYesNoControl
                                                label={getText(`Flowsheets_AsthmaBasicCareComponentLabel_${careElement.Name}`)}
                                                key={`${careElement.Name}_${state.Id}`}
                                                element={careElement}
                                                onChange={careElement => {
                                                    mutateState(state => {
                                                        state.CareElements = [...state.CareElements];
                                                        const index = state.CareElements.findIndex(p => p.Name === careElement.Name);
                                                        if (index !== -1) {
                                                            state.CareElements[index] = careElement;
                                                        }
                                                    });
                                                }}
                                                isEditable={isEditable}
                                                side="column"
                                            />
                                        ) : (
                                            <CareElementDateReviewedCheckbox
                                                key={`${careElement.Name}-${state.Id}`}
                                                label={getText(`Flowsheets_AsthmaBasicCareComponentLabel_${careElement.Name}`)}
                                                isEditable={isEditable}
                                                element={careElement}
                                                onChange={element => mutateState(state => {
                                                    state.CareElements = [...state.CareElements];
                                                    const index = state.CareElements.findIndex(p => p.Name === element.Name);
                                                    if (index !== -1) {
                                                        state.CareElements[index] = element;
                                                    }
                                                })}
                                            />
                                        )

                                    );
                                }
                                else if (careElement.CareElementType === CareElementType.MultiSelectReviewed) {
                                    return (
                                        <CareElementOptionsControl
                                            key={`${careElement.Name}-${state.Id}`}
                                            label={getText(`Flowsheets_AsthmaBasicCareComponentLabel_${careElement.Name}`)}
                                            isEditable={isEditable}
                                            element={careElement}
                                            onChange={element => mutateState(state => {
                                                state.CareElements = [...state.CareElements];
                                                const index = state.CareElements.findIndex(p => p.Name === element.Name);
                                                if (index !== -1) {
                                                    state.CareElements[index] = element;
                                                }
                                            })}
                                            side="column"
                                            options={[
                                                new InlineOption(FlowsheetConstants.AsthmaBasic.Monitoring.ActionPlanProvidedOptionName, getText("ProvidedLabel")),
                                                new InlineOption(FlowsheetConstants.AsthmaBasic.Monitoring.ActionPlanRevisedOptionName, getText("RevisedLabel")),
                                                new InlineOption(FlowsheetConstants.AsthmaBasic.Monitoring.ActionPlanReviewedOptionName, getText("ReviewedLabel"))
                                            ]}
                                        />
                                    );
                                }
                                return null;
                            }))}
                        </ReviewedCareElementContainer>
                    );
                })}
                <CareComponentNotes
                    id={`asthmaBasicCareComponentNotes-${state.Id}`}
                    dataCy="AsthmaBasicCareComponentNotes"
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    }
);

export { AsthmaBasicCareComponent };
